import { alpha2ToAlpha3 } from "@connect/i18n";
import type { components } from "@connect/data/public/schema";
import type { KycDraft } from "./kyc-draft-context";
import type { KycScreenKey } from "./kyc-screen-keys";

type UpdateIdentityRequestBody = components["schemas"]["UpdateIdentityRequestBody"];
type Requirements = components["schemas"]["Requirements"];
type RequirementEntry = components["schemas"]["RequirementEntry"];
type IdentityBasicDetailsSubmission = components["schemas"]["IdentityBasicDetailsSubmission"];
type IdentityResidentialAddressSubmission =
  components["schemas"]["IdentityResidentialAddressSubmission"];
type IdentityQuestionnaireSubmission = components["schemas"]["IdentityQuestionnaireSubmission"];
type CddEmploymentStatus = components["schemas"]["CddEmploymentStatus"];
type CddAccountPurpose = components["schemas"]["CddAccountPurpose"];
type CddSourceOfWealth = components["schemas"]["CddSourceOfWealth"];
type EddProfession = components["schemas"]["EddProfession"];
type MonetaryAmount = components["schemas"]["MonetaryAmount"];
type Currency = MonetaryAmount["currency"];

const EEA_COUNTRIES = new Set([
  "AUT",
  "BEL",
  "BGR",
  "CYP",
  "CZE",
  "DEU",
  "DNK",
  "ESP",
  "EST",
  "FIN",
  "FRA",
  "GRC",
  "HRV",
  "HUN",
  "IRL",
  "ITA",
  "LTU",
  "LUX",
  "LVA",
  "MLT",
  "NLD",
  "POL",
  "PRT",
  "ROU",
  "SVK",
  "SVN",
  "SWE",
  "ISL",
  "LIE",
  "NOR",
]);

function isPresent(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== "";
}

function toE164(phone: string): string {
  let digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return `+1${digits}`;
}

function toIsoDate(value: string): string {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  const [month, day, year] = trimmed.split("/");
  if (!month || !day || !year) {
    return trimmed;
  }
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

export function toAlpha3(code: string): string {
  const upper = code.trim().toUpperCase();
  return upper.length === 2 ? alpha2ToAlpha3(upper) : upper;
}

function toDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function omitEmpty<T extends object>(value: T): T | undefined {
  const entries = Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== "");
  if (entries.length === 0) {
    return undefined;
  }
  return Object.fromEntries(entries) as T;
}

export function reportingCurrency(country: string | undefined): Currency {
  const code = toAlpha3(country ?? "");

  if (code === "AUS") {
    return "AUD";
  }

  if (code === "NZL") {
    return "NZD";
  }

  if (code === "GBR") {
    return "GBP";
  }

  return EEA_COUNTRIES.has(code) ? "EUR" : "USD";
}

export function draftToPatchBody(
  draft: KycDraft,
  requirements?: Requirements,
): UpdateIdentityRequestBody {
  const body: UpdateIdentityRequestBody = {};
  const shouldSubmit = (requirement: RequirementEntry | undefined) =>
    requirements === undefined || requirement?.status === "incomplete";

  if (shouldSubmit(requirements?.phoneNumber) && isPresent(draft.phoneNumber)) {
    body.phoneNumber = { number: toE164(draft.phoneNumber) };
  }

  const nationalityRequired = requirements?.basicDetails?.requiredFields?.includes("nationality");
  const nationality =
    draft.basicDetails?.nationality ??
    (nationalityRequired ? draft.residentialAddress?.country : undefined);

  const basicDetails = omitEmpty<IdentityBasicDetailsSubmission>({
    firstName: isPresent(draft.basicDetails?.firstName)
      ? draft.basicDetails.firstName.trim()
      : undefined,
    lastName: isPresent(draft.basicDetails?.lastName)
      ? draft.basicDetails.lastName.trim()
      : undefined,
    dateOfBirth: isPresent(draft.basicDetails?.dateOfBirth)
      ? toIsoDate(draft.basicDetails.dateOfBirth)
      : undefined,
    nationality: isPresent(nationality) ? toAlpha3(nationality) : undefined,
  });
  if (shouldSubmit(requirements?.basicDetails) && basicDetails) {
    body.basicDetails = basicDetails;
  }

  const residentialAddress = omitEmpty<IdentityResidentialAddressSubmission>({
    street: isPresent(draft.residentialAddress?.street)
      ? draft.residentialAddress.street.trim()
      : undefined,
    subStreet: isPresent(draft.residentialAddress?.subStreet)
      ? draft.residentialAddress.subStreet.trim()
      : undefined,
    locality: isPresent(draft.residentialAddress?.locality)
      ? draft.residentialAddress.locality.trim()
      : undefined,
    postalCode: isPresent(draft.residentialAddress?.postalCode)
      ? draft.residentialAddress.postalCode.trim()
      : undefined,
    administrativeArea: isPresent(draft.residentialAddress?.administrativeArea)
      ? draft.residentialAddress.administrativeArea.trim()
      : undefined,
    country: isPresent(draft.residentialAddress?.country)
      ? toAlpha3(draft.residentialAddress.country)
      : undefined,
  });
  if (shouldSubmit(requirements?.residentialAddress) && residentialAddress) {
    body.residentialAddress = residentialAddress;
  }

  const taxIdentifiers = draft.taxIdentifiers
    ?.filter((entry) => isPresent(entry.value))
    .map((entry) => ({ type: entry.type, value: toDigits(entry.value) }))
    .filter((entry) => isPresent(entry.value));
  if (shouldSubmit(requirements?.taxIdentifiers) && taxIdentifiers && taxIdentifiers.length > 0) {
    body.taxIdentifiers = taxIdentifiers;
  }

  const currency = reportingCurrency(draft.residentialAddress?.country);
  const outstandingQuestionnaires = requirements?.questionnaires?.requiredFields;

  const questionnaires = (draft.questionnaires ?? [])
    .filter(
      (entry) =>
        !outstandingQuestionnaires ||
        outstandingQuestionnaires.some((field) => field === entry.type),
    )
    .map((entry) => toQuestionnaireSubmission(entry, currency))
    .filter((entry): entry is IdentityQuestionnaireSubmission => entry !== undefined);

  if (shouldSubmit(requirements?.questionnaires) && questionnaires.length > 0) {
    body.questionnaires = questionnaires;
  }

  return body;
}

const SUBMITTING_SCREENS = new Set<KycScreenKey>([
  "legal-name",
  "dob-nationality",
  "address-manual",
  "ssn",
  "due-diligence",
  "enhanced-due-diligence",
]);

export function isSubmittingScreen(key: KycScreenKey): boolean {
  return SUBMITTING_SCREENS.has(key);
}

function pickBasicDetails(
  body: UpdateIdentityRequestBody,
  fields: (keyof IdentityBasicDetailsSubmission)[],
): UpdateIdentityRequestBody {
  const basicDetails = omitEmpty<IdentityBasicDetailsSubmission>(
    Object.fromEntries(fields.map((field) => [field, body.basicDetails?.[field]])),
  );

  return basicDetails ? { basicDetails } : {};
}

function pickQuestionnaire(
  body: UpdateIdentityRequestBody,
  type: string,
): UpdateIdentityRequestBody {
  const questionnaires = body.questionnaires?.filter((entry) => entry.type === type);

  return questionnaires && questionnaires.length > 0 ? { questionnaires } : {};
}

export function screenPatchBody(
  key: KycScreenKey,
  draft: KycDraft,
  requirements?: Requirements,
): UpdateIdentityRequestBody {
  const body = draftToPatchBody(draft, requirements);

  switch (key) {
    case "legal-name":
      return pickBasicDetails(body, ["firstName", "lastName"]);

    case "dob-nationality":
      return pickBasicDetails(body, ["firstName", "lastName", "dateOfBirth", "nationality"]);

    case "address-manual":
      return body.residentialAddress ? { residentialAddress: body.residentialAddress } : {};

    case "ssn":
      return body.taxIdentifiers ? { taxIdentifiers: body.taxIdentifiers } : {};

    case "due-diligence":
      return pickQuestionnaire(body, "customerDueDiligence");

    case "enhanced-due-diligence":
      return pickQuestionnaire(body, "enhancedDueDiligence");

    default:
      return {};
  }
}

function toMonetaryAmount(
  value: string | undefined,
  currency: Currency,
): MonetaryAmount | undefined {
  if (!isPresent(value)) {
    return undefined;
  }
  return { currency, amount: value.trim() };
}

function toQuestionnaireSubmission(
  entry: {
    type: string;
    answers: Record<string, string>;
  },
  currency: Currency,
): IdentityQuestionnaireSubmission | undefined {
  if (entry.type === "customerDueDiligence") {
    return toCustomerDueDiligenceSubmission(entry.answers, currency);
  }
  if (entry.type === "enhancedDueDiligence") {
    return toEnhancedDueDiligenceSubmission(entry.answers, currency);
  }
  return undefined;
}

function toCustomerDueDiligenceSubmission(
  answers: Record<string, string>,
  currency: Currency,
): IdentityQuestionnaireSubmission | undefined {
  const employmentStatus = answers.employmentStatus;
  const accountPurpose = answers.accountPurpose;
  const sourceOfWealth = answers.sourceOfWealth;
  const grossAnnualIncome = toMonetaryAmount(answers.grossAnnualIncome, currency);
  const expectedTransactionAmountPerMonth = toMonetaryAmount(
    answers.expectedTransactionAmountPerMonth,
    currency,
  );
  const transactionFrequencyPerMonth = Number.parseInt(
    answers.transactionFrequencyPerMonth ?? "",
    10,
  );
  if (
    !isPresent(employmentStatus) ||
    !isPresent(accountPurpose) ||
    !isPresent(sourceOfWealth) ||
    !grossAnnualIncome ||
    !expectedTransactionAmountPerMonth ||
    !Number.isInteger(transactionFrequencyPerMonth) ||
    transactionFrequencyPerMonth <= 0
  ) {
    return undefined;
  }
  return {
    type: "customerDueDiligence",
    answers: {
      employmentStatus: employmentStatus as CddEmploymentStatus,
      accountPurpose: accountPurpose as CddAccountPurpose,
      sourceOfWealth: sourceOfWealth as CddSourceOfWealth,
      grossAnnualIncome,
      transactionFrequencyPerMonth,
      expectedTransactionAmountPerMonth,
    },
  };
}

function toEnhancedDueDiligenceSubmission(
  answers: Record<string, string>,
  currency: Currency,
): IdentityQuestionnaireSubmission | undefined {
  const profession = answers.profession;
  const netWorth = toMonetaryAmount(answers.netWorth, currency);
  if (!isPresent(profession) || !netWorth) {
    return undefined;
  }
  return {
    type: "enhancedDueDiligence",
    answers: {
      profession: profession as EddProfession,
      netWorth,
    },
  };
}
